import React from 'react'
import QuestionCard from './QuestionCard'
import RadioOption from './RadioOption'
import CheckboxOption from './CheckboxOption'

interface SectionProps {
  formData: Record<string, any>
  updateField: (field: string, value: any) => void
}

/** Maps Q6 values to their display labels for Q19b dynamic filtering. */
const Q6_LABEL_MAP: Record<string, string> = {
  descricoes_textos: 'Descrições e textos de produtos',
  imagens_produtos: 'Imagens de produtos',
  precificacao: 'Precificação e monitoramento de preços',
  anuncios_trafego: 'Anúncios e tráfego pago',
  atendimento: 'Atendimento ao cliente',
  emails_crm: 'Emails e CRM',
  analise_dados: 'Análise de dados e relatórios',
  gestao_estoque: 'Gestão de estoque e previsão de demanda',
  seo: 'SEO',
  gestao_marketplaces: 'Gestão de marketplaces',
  reclame_aqui: 'Reclame Aqui / reputação',
}

/**
 * Section F: Future (Q18-Q20)
 *
 * Captures investment intentions, desired AI areas (both new and
 * expanding existing usage), and top concerns about AI in e-commerce.
 */
const SectionF: React.FC<SectionProps> = ({ formData, updateField }) => {
  const q6Areas: string[] = Array.isArray(formData.q6) ? formData.q6 : []
  const q19aValues: string[] = Array.isArray(formData.q19a) ? formData.q19a : []
  const q19bValues: string[] = Array.isArray(formData.q19b) ? formData.q19b : []

  const handleCheckboxToggle = (
    field: string,
    currentValues: string[],
    value: string,
    checked: boolean,
  ) => {
    if (checked) {
      updateField(field, [...currentValues, value])
    } else {
      updateField(field, currentValues.filter((v) => v !== value))
    }
  }

  return (
    <div className="flex flex-col gap-6">

      {/* Q18 */}
      <QuestionCard
        title="Nos próximos 12 meses, você pretende:"
        required
      >
        <RadioOption
          name="q18"
          value="investir_significativamente"
          label="Investir significativamente mais em IA"
          selected={formData.q18 === 'investir_significativamente'}
          onChange={(value) => updateField('q18', value)}
        />
        <RadioOption
          name="q18"
          value="investir_pouco_mais"
          label="Investir um pouco mais"
          selected={formData.q18 === 'investir_pouco_mais'}
          onChange={(value) => updateField('q18', value)}
        />
        <RadioOption
          name="q18"
          value="manter_nivel"
          label="Manter o nível atual"
          selected={formData.q18 === 'manter_nivel'}
          onChange={(value) => updateField('q18', value)}
        />
        <RadioOption
          name="q18"
          value="reduzir"
          label="Reduzir investimento em IA"
          selected={formData.q18 === 'reduzir'}
          onChange={(value) => updateField('q18', value)}
        />
        <RadioOption
          name="q18"
          value="nao_tenho_planos"
          label="Não tenho planos definidos"
          selected={formData.q18 === 'nao_tenho_planos'}
          onChange={(value) => updateField('q18', value)}
        />
      </QuestionCard>

      {/* Q19a */}
      <QuestionCard
        title="Quais áreas você GOSTARIA de usar IA mas AINDA NÃO usa?"
      >
        <CheckboxOption
          name="q19a"
          value="descricoes_textos"
          label="Descrições e textos de produtos"
          checked={q19aValues.includes('descricoes_textos')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
        <CheckboxOption
          name="q19a"
          value="imagens_produtos"
          label="Imagens de produtos"
          checked={q19aValues.includes('imagens_produtos')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
        <CheckboxOption
          name="q19a"
          value="precificacao"
          label="Precificação e monitoramento de preços"
          checked={q19aValues.includes('precificacao')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
        <CheckboxOption
          name="q19a"
          value="anuncios_trafego"
          label="Anúncios e tráfego pago"
          checked={q19aValues.includes('anuncios_trafego')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
        <CheckboxOption
          name="q19a"
          value="atendimento"
          label="Atendimento ao cliente"
          checked={q19aValues.includes('atendimento')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
        <CheckboxOption
          name="q19a"
          value="emails_crm"
          label="Emails e CRM"
          checked={q19aValues.includes('emails_crm')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
        <CheckboxOption
          name="q19a"
          value="analise_dados"
          label="Análise de dados e relatórios"
          checked={q19aValues.includes('analise_dados')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
        <CheckboxOption
          name="q19a"
          value="gestao_estoque"
          label="Gestão de estoque e previsão de demanda"
          checked={q19aValues.includes('gestao_estoque')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
        <CheckboxOption
          name="q19a"
          value="seo"
          label="SEO"
          checked={q19aValues.includes('seo')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
        <CheckboxOption
          name="q19a"
          value="gestao_marketplaces"
          label="Gestão de marketplaces"
          checked={q19aValues.includes('gestao_marketplaces')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
        <CheckboxOption
          name="q19a"
          value="agentes_autonomos"
          label="Agentes de IA autônomos que executam tarefas operacionais"
          checked={q19aValues.includes('agentes_autonomos')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
        <CheckboxOption
          name="q19a"
          value="ia_insights_cruzados"
          label="IA que conecta dados de múltiplos sistemas para insights cruzados"
          checked={q19aValues.includes('ia_insights_cruzados')}
          onChange={(value, checked) => handleCheckboxToggle('q19a', q19aValues, value, checked)}
        />
      </QuestionCard>

      {/* Q19b — only rendered when the respondent selected areas in Q6 */}
      {q6Areas.length > 0 && (
        <QuestionCard
          title="Quais áreas você JÁ USA IA e gostaria de usar AINDA MAIS?"
        >
          {q6Areas.map((areaValue) => {
            const areaLabel = Q6_LABEL_MAP[areaValue]
            if (!areaLabel) return null

            return (
              <CheckboxOption
                key={areaValue}
                name="q19b"
                value={areaValue}
                label={areaLabel}
                checked={q19bValues.includes(areaValue)}
                onChange={(value, checked) =>
                  handleCheckboxToggle('q19b', q19bValues, value, checked)
                }
              />
            )
          })}
        </QuestionCard>
      )}

      {/* Q20 */}
      <QuestionCard
        title="Qual sua maior preocupação com o avanço da IA no e-commerce?"
        required
      >
        <RadioOption
          name="q20"
          value="concorrentes_antes"
          label="Meus concorrentes vão usar IA antes de mim"
          selected={formData.q20 === 'concorrentes_antes'}
          onChange={(value) => updateField('q20', value)}
        />
        <RadioOption
          name="q20"
          value="substituir_funcoes"
          label="IA vai substituir funções da minha equipe"
          selected={formData.q20 === 'substituir_funcoes'}
          onChange={(value) => updateField('q20', value)}
        />
        <RadioOption
          name="q20"
          value="nao_consigo_acompanhar"
          label="Não consigo acompanhar a velocidade de mudança"
          selected={formData.q20 === 'nao_consigo_acompanhar'}
          onChange={(value) => updateField('q20', value)}
        />
        <RadioOption
          name="q20"
          value="qualidade_confiabilidade"
          label="Qualidade e confiabilidade da IA"
          selected={formData.q20 === 'qualidade_confiabilidade'}
          onChange={(value) => updateField('q20', value)}
        />
        <RadioOption
          name="q20"
          value="dependencia_tecnologia"
          label="Dependência excessiva de tecnologia"
          selected={formData.q20 === 'dependencia_tecnologia'}
          onChange={(value) => updateField('q20', value)}
        />
        <RadioOption
          name="q20"
          value="sem_preocupacoes"
          label="Não tenho preocupações relevantes"
          selected={formData.q20 === 'sem_preocupacoes'}
          onChange={(value) => updateField('q20', value)}
        />
      </QuestionCard>
    </div>
  )
}

export default SectionF
